import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    Plus, 
    Search, 
    Edit2, 
    Trash2, 
    User,
    Mail,
    Phone,
    MapPin,
    Briefcase,
    X,
    Send,
    Users,
    FileText,
    Printer,
    Crown,
    KeyRound,
    Copy,
    Check,
    RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMMITTEE_ROLES = ['Member', 'Committee Chair', 'Vice Chair', 'Secretary'];

const MembersPage = () => {
    const [members, setMembers] = useState([]);
    const [committees, setCommittees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [emailFilter, setEmailFilter] = useState('all'); // 'all', 'sent', 'not_sent'
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, memberId: null, memberName: '' });
    const [committeeModalOpen, setCommitteeModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [committeeAssignments, setCommitteeAssignments] = useState([]);
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [passwordResetModal, setPasswordResetModal] = useState({ open: false, member: null });
    const [passwordResetForm, setPasswordResetForm] = useState({
        mode: 'generate', // 'generate' or 'manual'
        manualPassword: '',
        sendEmail: true
    });
    const [passwordResetResult, setPasswordResetResult] = useState(null);
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [copiedPassword, setCopiedPassword] = useState(false);
    const printRef = useRef();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        profession: '',
        location: '',
        interests: '',
        committee_ids: [],
        notes: ''
    });

    useEffect(() => {
        fetchMembers();
        fetchCommittees();
    }, []);

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/members`);
            setMembers(response.data);
        } catch (error) {
            toast.error('Failed to load members');
        } finally {
            setLoading(false);
        }
    };

    const fetchCommittees = async () => {
        try {
            const response = await axios.get(`${API}/committees`);
            setCommittees(response.data);
        } catch (error) {
            console.error('Failed to load committees');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                interests: formData.interests.split(',').map(i => i.trim()).filter(Boolean)
            };

            if (editingMember) {
                await axios.put(`${API}/members/${editingMember.id}`, payload);
                toast.success('Member updated successfully');
            } else {
                await axios.post(`${API}/members`, payload);
                toast.success('Member added successfully');
            }
            setIsModalOpen(false);
            resetForm();
            fetchMembers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save member');
        }
    };

    const handleEdit = (member) => {
        setEditingMember(member);
        setFormData({
            name: member.name,
            email: member.email,
            phone: member.phone || '',
            profession: member.profession || '',
            location: member.location || '',
            interests: member.interests?.join(', ') || '',
            committee_ids: member.committee_ids || [],
            notes: member.notes || ''
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (memberId) => {
        try {
            await axios.delete(`${API}/members/${memberId}`);
            toast.success('Member deleted successfully');
            setDeleteConfirm({ open: false, memberId: null, memberName: '' });
            fetchMembers();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete member');
        }
    };

    const openDeleteConfirm = (member) => {
        setDeleteConfirm({ open: true, memberId: member.id, memberName: member.name || member.email });
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirm({ open: false, memberId: null, memberName: '' });
    };

    const resetForm = () => {
        setEditingMember(null);
        setFormData({
            name: '',
            email: '',
            phone: '',
            profession: '',
            location: '',
            interests: '',
            committee_ids: [],
            notes: ''
        });
    };

    const handleSendWelcomeEmail = async (member) => {
        try {
            const response = await axios.post(`${API}/members/${member.id}/send-welcome-email`);
            if (response.data.success) {
                toast.success(`Welcome email sent to ${member.email}`);
                // Refresh member list to update email status
                fetchMembers();
            } else {
                toast.error(response.data.message || 'Failed to send welcome email');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send welcome email');
        }
    };

    // Password Reset Functions
    const openPasswordResetModal = (member) => {
        setPasswordResetModal({ open: true, member });
        setPasswordResetForm({ mode: 'generate', manualPassword: '', sendEmail: true });
        setPasswordResetResult(null);
        setCopiedPassword(false);
    };

    const closePasswordResetModal = () => {
        setPasswordResetModal({ open: false, member: null });
        setPasswordResetForm({ mode: 'generate', manualPassword: '', sendEmail: true });
        setPasswordResetResult(null);
        setCopiedPassword(false);
    };

    const handlePasswordReset = async () => {
        const { member } = passwordResetModal;
        if (!member) return;

        // Validate manual password if in manual mode
        if (passwordResetForm.mode === 'manual') {
            if (!passwordResetForm.manualPassword) {
                toast.error('Please enter a password');
                return;
            }
            if (passwordResetForm.manualPassword.length < 8) {
                toast.error('Password must be at least 8 characters');
                return;
            }
        }

        setIsResettingPassword(true);
        try {
            const response = await axios.post(`${API}/admin/members/${member.id}/reset-password`, {
                new_password: passwordResetForm.mode === 'manual' ? passwordResetForm.manualPassword : null,
                send_email: passwordResetForm.sendEmail
            });

            if (response.data.success) {
                setPasswordResetResult(response.data);
                toast.success(`Password reset for ${member.name || member.email}`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
        } finally {
            setIsResettingPassword(false);
        }
    };

    const copyPasswordToClipboard = () => {
        if (passwordResetResult?.new_password) {
            navigator.clipboard.writeText(passwordResetResult.new_password);
            setCopiedPassword(true);
            toast.success('Password copied to clipboard');
            setTimeout(() => setCopiedPassword(false), 3000);
        }
    };

    // Committee Assignment Functions
    const openCommitteeModal = (member) => {
        setSelectedMember(member);
        // Initialize with existing assignments or empty array
        const existingAssignments = member.committee_assignments || 
            (member.committee_ids || []).map(id => ({ committee_id: id, role: 'Member' }));
        setCommitteeAssignments(existingAssignments);
        setCommitteeModalOpen(true);
    };

    const addCommitteeAssignment = () => {
        setCommitteeAssignments([...committeeAssignments, { committee_id: '', role: 'Member' }]);
    };

    const updateAssignment = (index, field, value) => {
        const updated = [...committeeAssignments];
        updated[index] = { ...updated[index], [field]: value };
        setCommitteeAssignments(updated);
    };

    const removeAssignment = (index) => {
        setCommitteeAssignments(committeeAssignments.filter((_, i) => i !== index));
    };

    const saveCommitteeAssignments = async () => {
        try {
            // Filter out empty assignments
            const validAssignments = committeeAssignments.filter(a => a.committee_id);
            await axios.put(`${API}/members/${selectedMember.id}/committee-assignments`, {
                assignments: validAssignments
            });
            toast.success('Committee assignments saved');
            setCommitteeModalOpen(false);
            fetchMembers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save assignments');
        }
    };

    // Profile PDF Functions
    const openProfilePDF = async (member) => {
        try {
            const response = await axios.get(`${API}/members/${member.id}/profile-pdf`);
            setProfileData(response.data);
            setProfileModalOpen(true);
        } catch (error) {
            toast.error('Failed to load profile data');
        }
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Member Profile - ${profileData?.name}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                        h1 { color: #3D4435; border-bottom: 3px solid #C9A227; padding-bottom: 10px; }
                        h2 { color: #3D4435; margin-top: 30px; font-size: 18px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
                        .section { margin-bottom: 20px; }
                        .field { margin: 10px 0; }
                        .label { font-weight: bold; color: #666; }
                        .value { margin-left: 10px; }
                        .response-box { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-top: 5px; white-space: pre-wrap; }
                        .badge { display: inline-block; background: #3D4435; color: white; padding: 4px 12px; border-radius: 20px; margin: 4px; font-size: 12px; }
                        .committee-badge { background: #C9A227; color: #3D4435; }
                        @media print { body { padding: 20px; } }
                    </style>
                </head>
                <body>
                    ${printContent.innerHTML}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const filteredMembers = members.filter(member => {
        // Search filter
        const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.profession?.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Email status filter
        let matchesEmailFilter = true;
        if (emailFilter === 'sent') {
            matchesEmailFilter = member.welcome_email_sent === true;
        } else if (emailFilter === 'not_sent') {
            matchesEmailFilter = member.welcome_email_sent !== true;
        }
        
        return matchesSearch && matchesEmailFilter;
    });

    // Count members by email status
    const emailStats = {
        total: members.length,
        sent: members.filter(m => m.welcome_email_sent === true).length,
        notSent: members.filter(m => m.welcome_email_sent !== true).length
    };

    const getCommitteeName = (id) => {
        const committee = committees.find(c => c.id === id);
        return committee?.name || id;
    };

    const getCommitteeColor = (id) => {
        const committee = committees.find(c => c.id === id);
        return committee?.color || '#845642';
    };

    const toggleCommittee = (committeeId) => {
        setFormData(prev => ({
            ...prev,
            committee_ids: prev.committee_ids.includes(committeeId)
                ? prev.committee_ids.filter(id => id !== committeeId)
                : [...prev.committee_ids, committeeId]
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-[#A0A0A0]">Loading members...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="members-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-white font-['Playfair_Display'] tracking-tight">
                        Members
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Manage your Recovery Room community
                    </p>
                </div>
                <Dialog open={isModalOpen} onOpenChange={(open) => {
                    setIsModalOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button 
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                            data-testid="add-member-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Member
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-white font-['Playfair_Display']">
                                {editingMember ? 'Edit Member' : 'Add New Member'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        placeholder="John Doe"
                                        data-testid="member-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email *</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        placeholder="john@example.com"
                                        data-testid="member-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone</Label>
                                    <Input
                                        id="phone"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="(555) 123-4567"
                                        data-testid="member-phone-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="profession">Profession</Label>
                                    <Input
                                        id="profession"
                                        value={formData.profession}
                                        onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                                        placeholder="Software Engineer"
                                        data-testid="member-profession-input"
                                    />
                                </div>
                                <div className="space-y-2 sm:col-span-2">
                                    <Label htmlFor="location">Location</Label>
                                    <Input
                                        id="location"
                                        value={formData.location}
                                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                        placeholder="Washington, D.C."
                                        data-testid="member-location-input"
                                    />
                                </div>
                                <div className="space-y-2 sm:col-span-2">
                                    <Label htmlFor="interests">Interests (comma-separated)</Label>
                                    <Input
                                        id="interests"
                                        value={formData.interests}
                                        onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                                        placeholder="wellness, mentorship, networking"
                                        data-testid="member-interests-input"
                                    />
                                </div>
                                <div className="space-y-2 sm:col-span-2">
                                    <Label>Committees</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {committees.map((committee) => (
                                            <button
                                                key={committee.id}
                                                type="button"
                                                onClick={() => toggleCommittee(committee.id)}
                                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                                    formData.committee_ids.includes(committee.id)
                                                        ? 'text-white'
                                                        : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#E6E1D8]'
                                                }`}
                                                style={formData.committee_ids.includes(committee.id) 
                                                    ? { backgroundColor: committee.color } 
                                                    : {}
                                                }
                                                data-testid={`committee-toggle-${committee.id}`}
                                            >
                                                {committee.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-2 sm:col-span-2">
                                    <Label htmlFor="notes">Notes</Label>
                                    <Textarea
                                        id="notes"
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        placeholder="Any additional notes about this member..."
                                        rows={3}
                                        data-testid="member-notes-input"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                    data-testid="cancel-member-btn"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit"
                                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                    data-testid="save-member-btn"
                                >
                                    {editingMember ? 'Update Member' : 'Add Member'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0A0A0]" />
                    <Input
                        placeholder="Search members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="search-members-input"
                    />
                </div>
                
                {/* Welcome Email Filter */}
                <div className="flex items-center gap-2">
                    <span className="text-sm text-[#A0A0A0]">Filter:</span>
                    <div className="flex rounded-lg overflow-hidden border border-[#5A6350]">
                        <button
                            onClick={() => setEmailFilter('all')}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                emailFilter === 'all' 
                                    ? 'bg-[#C9A227] text-[#3D4435]' 
                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                            data-testid="filter-all"
                        >
                            All ({emailStats.total})
                        </button>
                        <button
                            onClick={() => setEmailFilter('sent')}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-[#5A6350] ${
                                emailFilter === 'sent' 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                            data-testid="filter-email-sent"
                        >
                            Email Sent ({emailStats.sent})
                        </button>
                        <button
                            onClick={() => setEmailFilter('not_sent')}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-[#5A6350] ${
                                emailFilter === 'not_sent' 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                            data-testid="filter-email-not-sent"
                        >
                            Needs Email ({emailStats.notSent})
                        </button>
                    </div>
                </div>
            </div>

            {/* Members Grid */}
            {filteredMembers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                    {filteredMembers.map((member) => (
                        <Card 
                            key={member.id} 
                            className="border-[#5A6350] shadow-sm card-hover"
                            data-testid={`member-card-${member.id}`}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-[#C9A227] flex items-center justify-center text-white font-semibold text-lg">
                                            {member.name?.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white">{member.name}</h3>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Badge 
                                                    variant={member.is_active ? 'default' : 'secondary'}
                                                    className={member.is_active 
                                                        ? 'bg-[#4A6B53] text-white' 
                                                        : 'bg-[#E6E1D8] text-[#A0A0A0]'
                                                    }
                                                >
                                                    {member.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                                <Badge 
                                                    className={member.welcome_email_sent 
                                                        ? 'bg-green-600/20 text-green-400 border border-green-600/30' 
                                                        : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                                                    }
                                                    title={member.welcome_email_sent_at 
                                                        ? `Sent: ${new Date(member.welcome_email_sent_at).toLocaleDateString()}` 
                                                        : 'Welcome email not sent'
                                                    }
                                                >
                                                    {member.welcome_email_sent ? 'Email Sent' : 'Needs Email'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => openCommitteeModal(member)}
                                            className="p-2 hover:bg-[#4A6B53]/20 rounded-lg transition-colors"
                                            title="Assign Committees"
                                            data-testid={`assign-committee-${member.id}`}
                                        >
                                            <Users className="w-4 h-4 text-[#4A6B53]" />
                                        </button>
                                        <button
                                            onClick={() => openProfilePDF(member)}
                                            className="p-2 hover:bg-[#845642]/20 rounded-lg transition-colors"
                                            title="View/Print Profile"
                                            data-testid={`view-profile-${member.id}`}
                                        >
                                            <FileText className="w-4 h-4 text-[#845642]" />
                                        </button>
                                        <button
                                            onClick={() => handleSendWelcomeEmail(member)}
                                            className={`p-2 rounded-lg transition-colors ${
                                                member.welcome_email_sent 
                                                    ? 'hover:bg-green-500/20' 
                                                    : 'hover:bg-orange-500/20 animate-pulse'
                                            }`}
                                            title={member.welcome_email_sent 
                                                ? `Resend Welcome Email (last sent: ${member.welcome_email_sent_at ? new Date(member.welcome_email_sent_at).toLocaleDateString() : 'unknown'})` 
                                                : 'Send Welcome Email (not yet sent)'
                                            }
                                            data-testid={`send-welcome-${member.id}`}
                                        >
                                            <Send className={`w-4 h-4 ${
                                                member.welcome_email_sent 
                                                    ? 'text-green-500' 
                                                    : 'text-orange-500'
                                            }`} />
                                        </button>
                                        <button
                                            onClick={() => openPasswordResetModal(member)}
                                            className="p-2 hover:bg-amber-500/20 rounded-lg transition-colors"
                                            title="Reset Password"
                                            data-testid={`reset-password-${member.id}`}
                                        >
                                            <KeyRound className="w-4 h-4 text-amber-600" />
                                        </button>
                                        <button
                                            onClick={() => handleEdit(member)}
                                            className="p-2 hover:bg-[#3D4435] rounded-lg transition-colors"
                                            data-testid={`edit-member-${member.id}`}
                                        >
                                            <Edit2 className="w-4 h-4 text-[#A0A0A0]" />
                                        </button>
                                        <button
                                            onClick={() => openDeleteConfirm(member)}
                                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                            data-testid={`delete-member-${member.id}`}
                                        >
                                            <Trash2 className="w-4 h-4 text-[#A64444]" />
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2 text-[#A0A0A0]">
                                        <Mail className="w-4 h-4" />
                                        <span className="truncate">{member.email}</span>
                                    </div>
                                    {member.phone && (
                                        <div className="flex items-center gap-2 text-[#A0A0A0]">
                                            <Phone className="w-4 h-4" />
                                            <span>{member.phone}</span>
                                        </div>
                                    )}
                                    {member.profession && (
                                        <div className="flex items-center gap-2 text-[#A0A0A0]">
                                            <Briefcase className="w-4 h-4" />
                                            <span>{member.profession}</span>
                                        </div>
                                    )}
                                    {member.location && (
                                        <div className="flex items-center gap-2 text-[#A0A0A0]">
                                            <MapPin className="w-4 h-4" />
                                            <span>{member.location}</span>
                                        </div>
                                    )}
                                </div>

                                {(member.committee_assignments?.length > 0 || member.committee_ids?.length > 0) && (
                                    <div className="flex flex-wrap gap-1.5 mt-4">
                                        {(member.committee_assignments || member.committee_ids?.map(id => ({ committee_id: id, role: 'Member' })) || []).map((assignment, idx) => {
                                            const cid = assignment.committee_id || assignment;
                                            const role = assignment.role || 'Member';
                                            const isChair = role === 'Committee Chair';
                                            return (
                                                <span
                                                    key={idx}
                                                    className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1"
                                                    style={{
                                                        backgroundColor: isChair ? '#C9A22730' : `${getCommitteeColor(cid)}15`,
                                                        color: isChair ? '#C9A227' : getCommitteeColor(cid),
                                                        border: `1px solid ${isChair ? '#C9A22750' : getCommitteeColor(cid) + '30'}`
                                                    }}
                                                >
                                                    {isChair && <Crown className="w-3 h-3" />}
                                                    {getCommitteeName(cid)}
                                                    {role !== 'Member' && <span className="opacity-70">({role})</span>}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#5A6350] text-xs text-[#A0A0A0]">
                                    <span>Attended: {member.attendance_count || 0}</span>
                                    <span>Joined: {new Date(member.join_date).toLocaleDateString()}</span>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card className="border-[#5A6350]">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <User className="w-16 h-16 text-[#E6E1D8] mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No members found</h3>
                        <p className="text-[#A0A0A0] text-center mb-4">
                            {searchTerm ? 'Try a different search term' : 'Add your first member to get started'}
                        </p>
                        {!searchTerm && (
                            <Button 
                                onClick={() => setIsModalOpen(true)}
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Member
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && closeDeleteConfirm()}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white font-['Playfair_Display']">
                            Delete Member
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-[#A0A0A0]">
                            Are you sure you want to delete <span className="text-white font-semibold">{deleteConfirm.memberName}</span>? 
                            This action cannot be undone.
                        </p>
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={closeDeleteConfirm}
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                            data-testid="cancel-delete-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleDelete(deleteConfirm.memberId)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="confirm-delete-btn"
                        >
                            Delete Member
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Committee Assignment Modal */}
            <Dialog open={committeeModalOpen} onOpenChange={setCommitteeModalOpen}>
                <DialogContent className="bg-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-[#3D4435] font-['Playfair_Display'] flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#4A6B53]" />
                            Assign Committees - {selectedMember?.name}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {committeeAssignments.map((assignment, index) => (
                            <div key={index} className="flex items-center gap-2 p-3 bg-[#F5F5F0] rounded-lg">
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                    <Select
                                        value={assignment.committee_id}
                                        onValueChange={(value) => updateAssignment(index, 'committee_id', value)}
                                    >
                                        <SelectTrigger className="bg-white">
                                            <SelectValue placeholder="Select Committee" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {committees.map((c) => (
                                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value={assignment.role}
                                        onValueChange={(value) => updateAssignment(index, 'role', value)}
                                    >
                                        <SelectTrigger className="bg-white">
                                            <SelectValue placeholder="Select Role" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {COMMITTEE_ROLES.map((role) => (
                                                <SelectItem key={role} value={role}>{role}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <button
                                    onClick={() => removeAssignment(index)}
                                    className="p-2 hover:bg-red-50 rounded-lg"
                                >
                                    <X className="w-4 h-4 text-[#A64444]" />
                                </button>
                            </div>
                        ))}

                        <Button
                            type="button"
                            variant="outline"
                            onClick={addCommitteeAssignment}
                            className="w-full border-dashed border-[#5A6350]"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Committee Assignment
                        </Button>
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                        <Button variant="outline" onClick={() => setCommitteeModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={saveCommitteeAssignments}
                            className="bg-[#4A6B53] hover:bg-[#3D5A45] text-white"
                        >
                            Save Assignments
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Profile PDF Modal */}
            <Dialog open={profileModalOpen} onOpenChange={setProfileModalOpen}>
                <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-[#3D4435] font-['Playfair_Display'] flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-[#845642]" />
                                Member Profile - {profileData?.name}
                            </span>
                            <div className="flex gap-2">
                                <Button 
                                    onClick={() => {
                                        setProfileModalOpen(false);
                                        openPasswordResetModal(profileData);
                                    }}
                                    variant="outline"
                                    className="border-amber-600 text-amber-600 hover:bg-amber-50"
                                >
                                    <KeyRound className="w-4 h-4 mr-2" />
                                    Reset Password
                                </Button>
                                <Button 
                                    onClick={handlePrint}
                                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                >
                                    <Printer className="w-4 h-4 mr-2" />
                                    Print PDF
                                </Button>
                            </div>
                        </DialogTitle>
                    </DialogHeader>

                    {profileData && (
                        <div ref={printRef} className="space-y-6 p-4">
                            <h1 style={{ color: '#3D4435', borderBottom: '3px solid #C9A227', paddingBottom: '10px', marginBottom: '20px' }}>
                                Member Profile: {profileData.name}
                            </h1>

                            {/* Basic Info */}
                            <div className="section">
                                <h2 style={{ color: '#3D4435', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Basic Information</h2>
                                <div className="grid grid-cols-2 gap-4 mt-3">
                                    <div><span className="font-bold text-gray-600">Name:</span> {profileData.name}</div>
                                    <div><span className="font-bold text-gray-600">Username:</span> {profileData.username}</div>
                                    <div><span className="font-bold text-gray-600">Email:</span> {profileData.email}</div>
                                    <div><span className="font-bold text-gray-600">Phone:</span> {profileData.phone || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Location:</span> {profileData.location || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Birth City:</span> {profileData.birth_city || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Join Date:</span> {new Date(profileData.join_date).toLocaleDateString()}</div>
                                </div>
                            </div>

                            {/* Professional Info */}
                            <div className="section">
                                <h2 style={{ color: '#3D4435', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Professional Information</h2>
                                <div className="grid grid-cols-2 gap-4 mt-3">
                                    <div><span className="font-bold text-gray-600">Profession:</span> {profileData.profession || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Job Title:</span> {profileData.job_title || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Employer:</span> {profileData.employer || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Employment Status:</span> {profileData.employment_status || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Entrepreneur Status:</span> {profileData.entrepreneur_status || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Education:</span> {profileData.education || 'N/A'}</div>
                                </div>
                            </div>

                            {/* Personal Info */}
                            <div className="section">
                                <h2 style={{ color: '#3D4435', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Personal Information</h2>
                                <div className="grid grid-cols-2 gap-4 mt-3">
                                    <div><span className="font-bold text-gray-600">Marital Status:</span> {profileData.marital_status || 'N/A'}</div>
                                    <div><span className="font-bold text-gray-600">Has Children:</span> {profileData.has_children ? `Yes (${profileData.num_children})` : 'No'}</div>
                                    <div><span className="font-bold text-gray-600">Has Siblings:</span> {profileData.has_siblings ? `Yes (${profileData.num_siblings})` : 'No'}</div>
                                </div>
                            </div>

                            {/* Hobbies & Interests */}
                            {(profileData.hobbies?.length > 0 || profileData.interests?.length > 0) && (
                                <div className="section">
                                    <h2 style={{ color: '#3D4435', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Hobbies & Interests</h2>
                                    <div className="mt-3">
                                        {profileData.hobbies?.length > 0 && (
                                            <div className="mb-2">
                                                <span className="font-bold text-gray-600">Hobbies:</span>
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    {profileData.hobbies.map((h, i) => (
                                                        <span key={i} className="badge" style={{ display: 'inline-block', background: '#3D4435', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>{h}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {profileData.interests?.length > 0 && (
                                            <div>
                                                <span className="font-bold text-gray-600">Interests:</span>
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    {profileData.interests.map((i, idx) => (
                                                        <span key={idx} className="badge" style={{ display: 'inline-block', background: '#3D4435', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>{i}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Committee Assignments */}
                            {profileData.committee_assignments_enriched?.length > 0 && (
                                <div className="section">
                                    <h2 style={{ color: '#3D4435', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Committee Assignments</h2>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {profileData.committee_assignments_enriched.map((c, i) => (
                                            <span key={i} className="committee-badge" style={{ display: 'inline-block', background: '#C9A227', color: '#3D4435', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>
                                                {c.committee_name} {c.role !== 'Member' ? `(${c.role})` : ''}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Experience */}
                            <div className="section">
                                <h2 style={{ color: '#3D4435', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Experience & Background</h2>
                                <div className="space-y-3 mt-3">
                                    <div>
                                        <span className="font-bold text-gray-600">Previous Men's Group Experience:</span>
                                        <span className="ml-2">{profileData.previous_mens_group ? 'Yes' : 'No'}</span>
                                        {profileData.previous_mens_group_details && (
                                            <div className="response-box bg-gray-100 p-3 rounded-lg mt-2">{profileData.previous_mens_group_details}</div>
                                        )}
                                    </div>
                                    <div>
                                        <span className="font-bold text-gray-600">Therapy Experience:</span>
                                        <span className="ml-2">{profileData.therapy_experience ? 'Yes' : 'No'}</span>
                                        {profileData.therapy_details && (
                                            <div className="response-box bg-gray-100 p-3 rounded-lg mt-2">{profileData.therapy_details}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Profile Completion Responses */}
                            <div className="section">
                                <h2 style={{ color: '#3D4435', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Profile Completion Responses</h2>
                                <div className="space-y-4 mt-3">
                                    <div>
                                        <span className="font-bold text-gray-600">Why do you want to join Recovery Room?</span>
                                        <div className="response-box bg-gray-100 p-3 rounded-lg mt-2 whitespace-pre-wrap">{profileData.why_join || 'Not provided'}</div>
                                    </div>
                                    <div>
                                        <span className="font-bold text-gray-600">How did you hear about us?</span>
                                        <div className="response-box bg-gray-100 p-3 rounded-lg mt-2 whitespace-pre-wrap">{profileData.referral_source || 'Not provided'}</div>
                                    </div>
                                    <div>
                                        <span className="font-bold text-gray-600">Current Needs:</span>
                                        <div className="response-box bg-gray-100 p-3 rounded-lg mt-2 whitespace-pre-wrap">{profileData.current_needs || 'Not provided'}</div>
                                    </div>
                                    <div>
                                        <span className="font-bold text-gray-600">What challenges are you currently facing?</span>
                                        <div className="response-box bg-gray-100 p-3 rounded-lg mt-2 whitespace-pre-wrap">{profileData.challenges || 'Not provided'}</div>
                                    </div>
                                    <div>
                                        <span className="font-bold text-gray-600">How can we support your growth?</span>
                                        <div className="response-box bg-gray-100 p-3 rounded-lg mt-2 whitespace-pre-wrap">{profileData.support_growth || 'Not provided'}</div>
                                    </div>
                                    <div>
                                        <span className="font-bold text-gray-600">Is there anything you need immediate help with?</span>
                                        <div className="response-box bg-gray-100 p-3 rounded-lg mt-2 whitespace-pre-wrap">{profileData.immediate_help || 'Not provided'}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Bio */}
                            {profileData.bio && (
                                <div className="section">
                                    <h2 style={{ color: '#3D4435', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Bio</h2>
                                    <div className="response-box bg-gray-100 p-3 rounded-lg mt-2 whitespace-pre-wrap">{profileData.bio}</div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Password Reset Modal */}
            <Dialog open={passwordResetModal.open} onOpenChange={(open) => !open && closePasswordResetModal()}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-amber-600" />
                            Reset Password
                        </DialogTitle>
                    </DialogHeader>
                    
                    {passwordResetModal.member && !passwordResetResult && (
                        <div className="space-y-4">
                            <div className="p-3 bg-gray-100 rounded-lg">
                                <p className="text-sm text-gray-600">Resetting password for:</p>
                                <p className="font-medium">{passwordResetModal.member.name || 'Unknown'}</p>
                                <p className="text-sm text-gray-500">{passwordResetModal.member.email}</p>
                            </div>

                            {/* Password Mode Selection */}
                            <div className="space-y-3">
                                <Label>Password Option</Label>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="passwordMode"
                                            checked={passwordResetForm.mode === 'generate'}
                                            onChange={() => setPasswordResetForm({ ...passwordResetForm, mode: 'generate', manualPassword: '' })}
                                            className="w-4 h-4 text-amber-600"
                                        />
                                        <div>
                                            <p className="font-medium">Generate Random Password</p>
                                            <p className="text-sm text-gray-500">System will create a secure password</p>
                                        </div>
                                    </label>
                                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="passwordMode"
                                            checked={passwordResetForm.mode === 'manual'}
                                            onChange={() => setPasswordResetForm({ ...passwordResetForm, mode: 'manual' })}
                                            className="w-4 h-4 text-amber-600"
                                        />
                                        <div>
                                            <p className="font-medium">Set Password Manually</p>
                                            <p className="text-sm text-gray-500">Enter a specific password</p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Manual Password Input */}
                            {passwordResetForm.mode === 'manual' && (
                                <div className="space-y-2">
                                    <Label htmlFor="manualPassword">New Password</Label>
                                    <Input
                                        id="manualPassword"
                                        type="text"
                                        value={passwordResetForm.manualPassword}
                                        onChange={(e) => setPasswordResetForm({ ...passwordResetForm, manualPassword: e.target.value })}
                                        placeholder="Enter new password (min 8 characters)"
                                        data-testid="manual-password-input"
                                    />
                                </div>
                            )}

                            {/* Send Email Checkbox */}
                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={passwordResetForm.sendEmail}
                                    onChange={(e) => setPasswordResetForm({ ...passwordResetForm, sendEmail: e.target.checked })}
                                    className="w-4 h-4 text-amber-600 rounded"
                                />
                                <div>
                                    <p className="font-medium">Send Email Notification</p>
                                    <p className="text-sm text-gray-500">Email the new password to the member</p>
                                </div>
                            </label>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={closePasswordResetModal}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handlePasswordReset}
                                    disabled={isResettingPassword}
                                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                                    data-testid="confirm-reset-password-btn"
                                >
                                    {isResettingPassword ? (
                                        <>
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                            Resetting...
                                        </>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Result Display */}
                    {passwordResetResult && (
                        <div className="space-y-4">
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 text-green-700 mb-2">
                                    <Check className="w-5 h-5" />
                                    <span className="font-medium">Password Reset Successful</span>
                                </div>
                                <p className="text-sm text-green-600">
                                    Password has been updated for {passwordResetModal.member?.name || passwordResetModal.member?.email}
                                </p>
                            </div>

                            <div className="p-4 bg-gray-100 rounded-lg">
                                <Label className="text-gray-600 mb-2 block">New Password</Label>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 p-3 bg-white border rounded font-mono text-lg tracking-wide">
                                        {passwordResetResult.new_password}
                                    </code>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={copyPasswordToClipboard}
                                        className="shrink-0"
                                        data-testid="copy-password-btn"
                                    >
                                        {copiedPassword ? (
                                            <Check className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            {passwordResetResult.email_sent ? (
                                <div className="flex items-center gap-2 text-sm text-green-600">
                                    <Mail className="w-4 h-4" />
                                    Email sent to {passwordResetResult.member_email}
                                </div>
                            ) : passwordResetForm.sendEmail && (
                                <div className="flex items-center gap-2 text-sm text-amber-600">
                                    <Mail className="w-4 h-4" />
                                    Email notification could not be sent
                                </div>
                            )}

                            <Button
                                onClick={closePasswordResetModal}
                                className="w-full"
                            >
                                Done
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MembersPage;
